import { ref, nextTick } from 'vue'
import { useMotion } from '@vueuse/motion'
import type { Ref } from 'vue'

interface SwipeNavigationOptions {
  swipeTarget: Ref<HTMLElement | null>
  cardRef: Ref<HTMLElement | null>
  onNext: () => void
  onPrevious: () => void
}

export function useSwipeNavigation(options: SwipeNavigationOptions) {
  console.log('SwipeNavigation: Initializing with options:', {
    swipeTarget: options.swipeTarget.value,
    cardRef: options.cardRef.value,
    hasOnNext: !!options.onNext,
    hasOnPrevious: !!options.onPrevious,
  })

  const windowWidth = ref(window.innerWidth)
  const swipeState = ref({
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    isDragging: false,
    startTime: 0,
    isClick: true,
  })

  const motion = useMotion(options.cardRef, {
    initial: { x: 0, opacity: 1 },
    enter: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 300, damping: 25 } },
  })

  // Track the last processed key event
  const lastProcessedKey = ref<{
    key: string
    timestamp: number
  } | null>(null)

  // Track if we've already added the keyboard listener
  const keyboardListenerAdded = ref(false)

  // Handle touch events
  const handleTouchStart = (e: TouchEvent) => {
    // Check if the touch target is an interactive element
    const target = e.target as HTMLElement
    if (target.closest('button, input, select, textarea, a, [role="button"]')) {
      console.log('SwipeNavigation: Touch on interactive element, allowing default behavior')
      return
    }

    console.log('SwipeNavigation: Touch start', {
      touch: e.touches[0],
      target: e.target,
      currentTarget: e.currentTarget,
      path: e.composedPath(),
      swipeState: swipeState.value,
    })

    swipeState.value.startX = e.touches[0].clientX
    swipeState.value.startY = e.touches[0].clientY
    swipeState.value.currentX = e.touches[0].clientX
    swipeState.value.currentY = e.touches[0].clientY
    swipeState.value.startTime = Date.now()
    swipeState.value.isDragging = true
    swipeState.value.isClick = true
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (!swipeState.value.isDragging) return

    const deltaX = e.touches[0].clientX - swipeState.value.startX
    const deltaY = e.touches[0].clientY - swipeState.value.startY
    const absDeltaX = Math.abs(deltaX)
    const absDeltaY = Math.abs(deltaY)

    console.log('SwipeNavigation: Touch move', {
      touch: e.touches[0],
      deltaX,
      deltaY,
      absDeltaX,
      absDeltaY,
      isHorizontal: absDeltaX > absDeltaY,
      target: e.target,
      currentTarget: e.currentTarget,
      path: e.composedPath(),
      swipeState: swipeState.value,
    })

    swipeState.value.currentX = e.touches[0].clientX
    swipeState.value.currentY = e.touches[0].clientY

    // If moved more than 5px, it's not a click
    if (absDeltaX > 5 || absDeltaY > 5) {
      swipeState.value.isClick = false
    }

    // Only prevent default and update card position for horizontal swipes
    if (absDeltaX > absDeltaY) {
      e.preventDefault()
      motion.set({ x: deltaX })
    }
  }

  const handleTouchEnd = async () => {
    if (!swipeState.value.isDragging) return

    const deltaX = swipeState.value.currentX - swipeState.value.startX
    const deltaTime = Date.now() - swipeState.value.startTime
    const velocity = Math.abs(deltaX) / deltaTime
    const threshold = windowWidth.value * 0.15
    const absDeltaX = Math.abs(deltaX)
    const absDeltaY = Math.abs(swipeState.value.currentY - swipeState.value.startY)

    console.log('SwipeNavigation: Touch end', {
      deltaX,
      deltaTime,
      velocity,
      threshold,
      absDeltaX,
      absDeltaY,
      isHorizontal: absDeltaX > absDeltaY,
      swipeState: swipeState.value,
    })

    // Determine if it's a swipe based on distance and velocity
    const isSwipe = absDeltaX > threshold || (absDeltaX > 50 && velocity > 0.3)

    if (isSwipe && !swipeState.value.isClick && absDeltaX > absDeltaY) {
      if (deltaX < 0) {
        console.log('SwipeNavigation: Swipe left detected')
        await animateSwipe(true)
        options.onNext()
      } else if (deltaX > 0) {
        console.log('SwipeNavigation: Swipe right detected')
        await animateSwipe(false)
        options.onPrevious()
      }
    } else {
      console.log('SwipeNavigation: Resetting position - not a valid swipe')
      motion.set({ x: 0 })
    }

    swipeState.value.isDragging = false
  }

  const animateSwipe = async (next: boolean) => {
    console.log('SwipeNavigation: Starting animation', { next })
    const direction = next ? -1 : 1
    await motion.set({ x: direction * windowWidth.value, opacity: 0 })
    await nextTick()
    await motion.set({ x: -direction * windowWidth.value, opacity: 0 })
    motion.apply('enter')
    console.log('SwipeNavigation: Animation complete')
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent) => {
    // Only handle left/right arrow keys
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

    // Check if the event target is an interactive element
    const target = e.target as HTMLElement
    if (
      target.closest(
        'input, textarea, select, button, [contenteditable="true"], [role="button"], [role="textbox"]',
      )
    ) {
      console.log('SwipeNavigation: Ignoring key event from interactive element')
      return
    }

    // Prevent event from bubbling up and prevent default behavior
    e.stopPropagation()
    e.preventDefault()
    e.stopImmediatePropagation()

    console.log('SwipeNavigation: Keyboard event captured', {
      key: e.key,
      target: e.target,
      currentTarget: e.currentTarget,
      eventPhase: e.eventPhase,
      timestamp: Date.now(),
      isTrusted: e.isTrusted,
      defaultPrevented: e.defaultPrevented,
    })

    // Check if this is a duplicate event
    const now = Date.now()
    if (
      lastProcessedKey.value &&
      lastProcessedKey.value.key === e.key &&
      now - lastProcessedKey.value.timestamp < 100 // 100ms cooldown
    ) {
      console.log('SwipeNavigation: Ignoring duplicate key event', {
        key: e.key,
        lastProcessed: lastProcessedKey.value,
        timeSinceLast: now - lastProcessedKey.value.timestamp,
      })
      return
    }

    // Update last processed key before processing
    lastProcessedKey.value = {
      key: e.key,
      timestamp: now,
    }

    // Process the key event
    switch (e.key) {
      case 'ArrowRight':
        console.log('SwipeNavigation: Keyboard next triggered')
        animateSwipe(true).then(() => {
          console.log('SwipeNavigation: Calling onNext callback')
          options.onNext()
        })
        break
      case 'ArrowLeft':
        console.log('SwipeNavigation: Keyboard previous triggered')
        animateSwipe(false).then(() => {
          console.log('SwipeNavigation: Calling onPrevious callback')
          options.onPrevious()
        })
        break
    }
  }

  const setup = () => {
    console.log('SwipeNavigation: Setting up event listeners')

    if (options.swipeTarget.value) {
      const target = options.swipeTarget.value
      console.log('SwipeNavigation: Adding event listeners to target:', {
        element: target,
        rect: target.getBoundingClientRect(),
        style: window.getComputedStyle(target),
      })

      // Add event listeners with capture phase
      const addTouchListener = (
        type: 'touchstart' | 'touchmove' | 'touchend',
        handler: (e: TouchEvent) => void,
      ) => {
        console.log(`SwipeNavigation: Adding ${type} listener`)
        target.addEventListener(type, handler as EventListener, { passive: false, capture: true })
      }

      addTouchListener('touchstart', handleTouchStart)
      addTouchListener('touchmove', handleTouchMove)
      addTouchListener('touchend', handleTouchEnd)

      // Add keyboard event listener at the document level with capture phase
      if (!keyboardListenerAdded.value) {
        console.log('SwipeNavigation: Adding keyboard event listener')
        document.addEventListener('keydown', handleKeyDown, { capture: true })
        keyboardListenerAdded.value = true
      } else {
        console.warn('SwipeNavigation: Keyboard listener already added')
      }
    } else {
      console.warn('SwipeNavigation: No swipe target element found')
    }
  }

  const cleanup = () => {
    console.log('SwipeNavigation: Cleaning up event listeners')
    if (options.swipeTarget.value) {
      const target = options.swipeTarget.value
      target.removeEventListener('touchstart', handleTouchStart)
      target.removeEventListener('touchmove', handleTouchMove)
      target.removeEventListener('touchend', handleTouchEnd)
    }
    if (keyboardListenerAdded.value) {
      document.removeEventListener('keydown', handleKeyDown)
      keyboardListenerAdded.value = false
    }
  }

  // Return the setup and cleanup functions
  return {
    setup,
    cleanup,
    swipeTarget: options.swipeTarget,
    cardRef: options.cardRef,
  }
}
